import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, X } from "@phosphor-icons/react";
import { getRecentlyViewed, clearRecentlyViewed } from "@/lib/recentlyViewed";

/**
 * Horizontal strip of the last few products this visitor viewed in the shop.
 * Hidden entirely when there's nothing to show.
 */
export default function RecentlyViewed({ limit = 4 }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(getRecentlyViewed(limit));
  }, [limit]);

  const clear = () => {
    clearRecentlyViewed();
    setItems([]);
  };

  if (!items || items.length === 0) return null;

  return (
    <section className="ji-container py-14" data-testid="recently-viewed">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="ji-overline mb-2 inline-flex items-center gap-2">
            <Clock size={12} weight="fill" /> Picking up where you left off
          </div>
          <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl md:text-4xl font-bold tracking-tight">
            Recently viewed
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="recently-viewed-clear"
            onClick={clear}
            className="text-xs uppercase tracking-[0.18em] text-[var(--ji-muted)] hover:text-[var(--ji-primary)] inline-flex items-center gap-1"
          >
            <X size={12} weight="bold" /> Clear
          </button>
          <Link
            to="/shop"
            data-testid="recently-viewed-shop-link"
            className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--ji-primary)] hover:text-[var(--ji-accent)] inline-flex items-center gap-1"
          >
            Back to shop <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((p) => (
          <Link
            key={p.id}
            to={`/shop?product=${p.id}`}
            data-testid={`recently-viewed-item-${p.id}`}
            className="ji-card flex flex-col overflow-hidden group hover:border-[var(--ji-accent)] transition-colors"
            style={{ borderRadius: 4 }}
          >
            <div className="relative aspect-[4/3] bg-[var(--ji-surface-alt)] overflow-hidden">
              <img
                src={p.image_url}
                alt={p.name}
                loading="lazy"
                onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=800&q=70"; }}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-4 flex flex-col flex-1">
              {p.brand && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)]">{p.brand}</div>
              )}
              <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-1 text-sm font-bold tracking-tight text-[var(--ji-text)] line-clamp-2">
                {p.name}
              </h3>
              {p.price != null && (
                <div className="mt-auto pt-3 flex items-baseline justify-between border-t border-[var(--ji-border)]">
                  <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-lg font-bold text-[var(--ji-primary)]">
                    ${Number(p.price).toFixed(2)}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--ji-accent)] font-semibold group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                    View <ArrowRight size={11} weight="bold" />
                  </span>
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
